import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSystemConfig } from '../../contexts/SystemConfigContext';
import { APP, STORAGE_KEYS } from '../../config/app';
import { ThemeToggle } from './ThemeToggle';
import BiometricOffer from './BiometricOffer';
import BiometricUnlock from './BiometricUnlock';
import { shouldOfferBiometric } from '../../utils/biometricAuth';
import './Login.css';
import { API_BASE_URL } from '../../utils/api';
import { readSignInNotice, clearSignInNotice } from '../../utils/signInNotice';


const PORTAL_PATHS = {
  master_admin: '/admin',
  department_admin: '/admin',
  head_of_department: '/hod',
  view_only_staff: '/view-only-staff',
  accounts_officer: '/accounts-officer',
  it_support: '/it-support',
  staff: '/staff',
};
const portalPathFor = (role) => PORTAL_PATHS[role] || '/student';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  // Why the app signed this person out, when it did so on purpose.
  const [notice] = useState(() => readSignInNotice());
  // Set only on a native device that can offer biometric sign-in. It holds the
  // account and the session that was just minted — never the password, which
  // the offer has no use for: accepting asks the server for a device token.
  const [offer, setOffer] = useState(null);
  // A biometric sign-in that succeeded but left a notice to acknowledge: the
  // user is already signed in, so the password form is put away.
  const [held, setHeld] = useState(false);
  const navigate = useNavigate();
  const { user, loading, setUser } = useAuth();
  const { config: sysConfig } = useSystemConfig();

  useEffect(() => {
    document.body.style.cssText = 'margin: 0 !important;';
    return () => { document.body.style.cssText = ''; };
  }, []);

  // A signed-in user who lands on "/" (the native app always launches here)
  // goes straight to their portal instead of seeing the form again.
  useEffect(() => {
    if (!loading && user) navigate(portalPathFor(user.role), { replace: true });
  }, [loading, user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) { setError('Please enter your email.'); return; }
    if (!password) { setError('Please enter your password.'); return; }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await response.json();
      if (!response.ok || !data.user) {
        setError(data.error || 'Invalid email or password.');
        return;
      }
      localStorage.removeItem(STORAGE_KEYS.LOGGED_OUT);
      clearSignInNotice();
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
      if (data.token) localStorage.setItem(STORAGE_KEYS.TOKEN, data.token);

      // Native only: ask once whether to save this sign-in behind biometrics.
      // The user is set after the answer, so the redirect effect waits.
      const { offer: askBiometric, label } = await shouldOfferBiometric(email);
      if (askBiometric) {
        setOffer({ email: email.trim().toLowerCase(), label, user: data.user });
        return;
      }

      setUser(data.user);
      navigate(portalPathFor(data.user.role));
    } catch (err) {
      console.error('Login failed:', err);
      setError('Could not connect to server.');
    }
  };

  // Stable identities: BiometricUnlock's unlock callback depends on these.
  const clearError = useCallback(() => setError(''), []);
  const holdForNotice = useCallback(() => setHeld(true), []);
  const goToPortal = useCallback(
    (u) => navigate(portalPathFor(u.role), { replace: true }),
    [navigate]
  );

  // The offer is the last step of a sign-in: whichever way it is answered, the
  // session that was already minted is handed to the app.
  const finishOffer = () => {
    const signedIn = offer.user;
    setOffer(null);
    setPassword('');
    setUser(signedIn);
    navigate(portalPathFor(signedIn.role));
  };

  return (
    <div className="login-page">
      {/* The login page has no portal header, so the appearance control
          lives here — the theme choice must be reachable before sign-in. */}
      <div className="login-appearance">
        <ThemeToggle showDensity={false} />
      </div>

      <div className="login-container">

        {/* Brand */}
        <div className="login-brand">
          <a href="https://www.ncad.ie" target="_blank" rel="noopener noreferrer" className="login-brand__link">
            <img src={sysConfig.customLogo || `${APP.basePath}/ncadLogo.gif`} alt={sysConfig.loginBrandName || 'Logo'} className="login-brand__mark" />
          </a>
          <div>
            <span className="login-brand__name">{sysConfig.loginBrandName || 'NCAD'}</span>
            <span className="login-brand__dept">{sysConfig.loginBrandDept || 'Bookings'}</span>
          </div>
        </div>

        {/* Login Form */}
        <div className="login-card">
          {offer ? (
            <BiometricOffer
              email={offer.email}
              label={offer.label}
              onDone={finishOffer}
            />
          ) : (
          <>
          <h1 className="login-card__title">Sign In</h1>
          <p className="login-card__subtitle">{sysConfig.systemSubtitle || 'Studios, rooms & equipment'}</p>

          {/* Native only: renders nothing unless this device has a saved sign-in. */}
          <BiometricUnlock
            onSignedIn={goToPortal}
            onAttempt={clearError}
            onHold={holdForNotice}
            quiet={Boolean(error)}
          />

          {!held && (
          <form onSubmit={handleLogin} noValidate>

            {notice && (
              <div className="login-notice" role="status">
                {notice}
              </div>
            )}

            {/* Email */}
            <div className="login-field">
              <label className="login-field__label" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className="login-field__input"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder="you@ncad.ie"
                autoComplete="email"
                autoFocus
              />
            </div>

            {/* Password */}
            <div className="login-field">
              <label className="login-field__label" htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                className="login-field__input"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="login-error" role="alert">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
                </svg>
                {error}
              </div>
            )}

            {/* Submit */}
            <button type="submit" className="login-submit">
              Sign In
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>

            {/* Forgot password */}
            <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
              <button
                type="button"
                className="unstyled"
                onClick={() => navigate('/forgot-password')}
                style={{ fontSize: 'var(--font-size-body-sm)', color: 'var(--text-secondary)', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
              >
                Forgot your password?
              </button>
            </div>

          </form>
          )}
          </>
          )}

        </div>

        <div className="login-footer">
          <p>&copy; 2026 National College of Art &amp; Design</p>
          <p style={{ marginTop: '0.25rem' }}>
            <button
              type="button"
              className="unstyled login-footer__link"
              onClick={() => navigate('/privacy')}
            >
              Privacy Notice
            </button>
            <span aria-hidden="true" style={{ fontSize: 'var(--font-size-caption)', color: 'var(--text-secondary)' }}> · </span>
            <button
              type="button"
              className="unstyled login-footer__link"
              onClick={() => navigate('/support')}
            >
              Help &amp; Support
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
